import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Link as MuiLink,
  Alert,
} from '@mui/material';
import { Link } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { ROUTES, APP_NAME } from '../config/constants';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { resetPassword, isLoading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await resetPassword(email);
    if (result.success) {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h1" gutterBottom textAlign="center" color="success.main">
              Check Your Email
            </Typography>
            
            <Alert severity="success" sx={{ mt: 3, mb: 3 }}>
              We've sent a password reset link to <strong>{email}</strong>
            </Alert>

            <Typography variant="body2" color="text.secondary" paragraph>
              Click the link in the email to reset your password. The link will expire in 1 hour.
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              Didn't receive the email? Check your spam folder or try again.
            </Typography>

            <Box textAlign="center" mt={4}>
              <Button
                variant="outlined"
                onClick={() => setSubmitted(false)}
                sx={{ mr: 2 }}
              >
                Send Another Email
              </Button>
              <Button
                component={Link}
                to={ROUTES.LOGIN}
                variant="contained"
              >
                Back to Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={ROUTES.LOGIN}
            sx={{ mb: 2 }}
          >
            Back to Login
          </Button>

          <Typography variant="h4" component="h1" gutterBottom textAlign="center" fontWeight="bold">
            {APP_NAME}
          </Typography>
          <Typography variant="h6" gutterBottom textAlign="center" color="text.secondary" mb={4}>
            Reset your password
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                Remember your password?{' '}
                <MuiLink component={Link} to={ROUTES.LOGIN} underline="hover">
                  Sign in
                </MuiLink>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};
